import {
  Cancel,
  Delete,
  Edit,
  GetApp as GetAppIcon,
  Save,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { Divider, IconButton, TextField, Typography } from '@mui/material';
import { showAlert } from 'lib/functions/utils';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import {
  deleteFileFromRequest,
  downloadFilesFromRequest,
  getFilesFromRequest,
  updateFileNameFromRequest,
  uploadFilesFromRequest,
} from '../../../../../../lib/api/files';
import DocumentsPreviewer, {
  useDocumentPreview,
} from '../../../../../../lib/common/DocumentsPreviewer';
import CenteredCircularProgress from '../../../../../../lib/designSystem/CircularProgress';
import HeaderWithActions, {
  CustomButtonProps,
} from '../../../../../../lib/designSystem/HeaderWithActions';
import MultiUploader from '../../../../../../lib/designSystem/MultiUploader';

const FileItem = (props: any) => {
  const {
    files,
    file,
    handleDownload,
    handlePreview,
    index,
    changeFileName,
    deleteFile,
    canPreviewFileName,
  } = props;
  const [editMode, setEditMode] = React.useState(false);
  const [newName, setNewName] = React.useState(file);
  const auth = useSelector((state: { auth: AuthState }) => state.auth);
  return (
    <div>
      <div key={file}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {editMode ? (
            <TextField
              autoFocus
              margin="dense"
              id="name"
              label="Nuevo nombre"
              type="text"
              fullWidth
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
          ) : (
            <Typography variant="subtitle1" gutterBottom component="div">
              - {file}
            </Typography>
          )}
          <div style={{ display: 'flex' }}>
            {editMode && !auth.user?.express && (
              <IconButton>
                <Delete onClick={() => deleteFile(file)} />
              </IconButton>
            )}
            {editMode && (
              <IconButton>
                <Save onClick={() => changeFileName(file, newName)} />
              </IconButton>
            )}
            {editMode && (
              <IconButton onClick={() => setEditMode(false)}>
                <Cancel />
              </IconButton>
            )}
            {!editMode && (
              <IconButton onClick={() => setEditMode(file)}>
                <Edit />
              </IconButton>
            )}
            {!editMode && (
              <IconButton
                onClick={() => handlePreview(file)}
                disabled={!canPreviewFileName(file)}
                title={
                  !canPreviewFileName(file)
                    ? 'Este tipo de archivo no se puede previsualizar'
                    : 'Previsualizar archivo'
                }
                sx={{
                  opacity: canPreviewFileName(file) ? 1 : 0.5,
                  '&:hover': {
                    opacity: canPreviewFileName(file) ? 0.8 : 0.5,
                  },
                }}
              >
                {canPreviewFileName(file) ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            )}
            {!editMode && (
              <IconButton onClick={() => handleDownload(file)}>
                <GetAppIcon />
              </IconButton>
            )}
          </div>
        </div>

        {index !== files.length - 1 && <Divider />}
      </div>
    </div>
  );
};

const FilesDialog = (props: any) => {
  const { openRequest, requestType, setOpenShoppingCart } = props;

  const dispatch = useDispatch();
  const [loading, setLoading] = React.useState(false);
  const [files, setFiles] = React.useState([]);
  const [addFilesMode, setAddFilesMode] = React.useState(false);
  const [filesAdded, setFilesAdded] = React.useState<any>([]);

  const {
    previewOpen,
    previewLoading,
    previewError,
    currentPreviewFile,
    blobUrl,
    contentType,
    handlePreview,
    handleClosePreview,
    setPreviewError,
    canPreviewFileName,
  } = useDocumentPreview({
    requestId: openRequest.id,
    requestType,
  });

  const handleDownload = async (fileName: any) => {
    setLoading(true);
    try {
      const data = {
        requestId: openRequest.id,
        fileName: fileName,
        requestType,
      };
      const response = await downloadFilesFromRequest(dispatch, data);
      if (response) {
        window.open(response);
      }
    } catch (error) {
      console.log(error);
    }
    setLoading(false);
  };

  const changeFileName = async (oldName: string, newName: string) => {
    setLoading(true);
    try {
      const data = {
        companyId: openRequest.companyReceiverId,
        oldName,
        newName,
        requestType,
        requestId: openRequest.id,
      };
      await updateFileNameFromRequest(dispatch, data);
      await setUpFiles();
      showAlert(
        dispatch,
        'success',
        'Se ha modificado el nombre de tu archivo con éxito',
      );
    } catch (error) {
      console.log(error);
    }
    setLoading(false);
  };

  const uploadFiles = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      filesAdded.forEach((file: any) => formData.append('files', file));
      await uploadFilesFromRequest(dispatch, formData, {
        requestId: openRequest.id,
        requestType,
      });
      await setUpFiles();
      setAddFilesMode(false);
      showAlert(dispatch, 'success', 'Se han subido tus archivos con éxito');
    } catch (error) {
      console.log(error);
    }
    setLoading(false);
  };

  const deleteFile = async (fileName: string) => {
    setLoading(true);
    try {
      await deleteFileFromRequest(dispatch, {
        requestId: openRequest.id,
        fileName,
        requestType,
      });
      await setUpFiles();
      showAlert(dispatch, 'success', 'Se ha eliminado el archivo con éxito');
    } catch (error) {
      console.log(error);
    }
    setLoading(false);
  };

  const setUpFiles = async () => {
    setLoading(true);
    try {
      const response = await getFilesFromRequest(dispatch, {
        requestId: openRequest.id,
        requestType,
      });
      setFiles(response);
    } catch (error) {
      console.log(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    setUpFiles();
  }, [openRequest]);

  const buttonsData: CustomButtonProps[] = [
    {
      action: () => setAddFilesMode(true),
      name: 'Agregar archivos',
      color: 'primary',
    },
  ];

  const addButtonsData: CustomButtonProps[] = [
    {
      action: () => setAddFilesMode(false),
      name: 'Cancelar',
      color: 'secondary',
    },
    {
      action: () => uploadFiles(),
      name: 'Guardar',
      disabled: filesAdded.length === 0,
      color: 'primary',
    },
  ];

  const handleChangeFile = (event: any) => {
    const newFiles = Array.from(event.target.files);
    setFilesAdded(newFiles); // Pass the file list to the parent component
  };

  return (
    <div style={{ minHeight: 200 }}>
      {loading ? (
        <CenteredCircularProgress type="dialog" />
      ) : (
        <div>
          <HeaderWithActions
            title={
              addFilesMode
                ? 'A continuación puedes subir archivos que te hagan falta, no olvides guardar'
                : 'Aquí se encuentran todos los archivos de la solicitud.'
            }
            variant="body1"
            buttons={addFilesMode ? addButtonsData : buttonsData}
          />
          {addFilesMode ? (
            <div>
              <div style={{ padding: '28px 0px' }}>
                <MultiUploader
                  buttonText="Agregar"
                  handleChange={handleChangeFile}
                  uploadedFileNames={filesAdded.map((x: any) => x.name)}
                  textLeft="Archivos subidos:"
                />
              </div>
            </div>
          ) : (
            <Typography padding={2} paragraph>
              {files.length === 0 && (
                <Typography> No hay archivos subidos. </Typography>
              )}
              {files.map((file: any, index: number) => (
                <FileItem
                  key={index}
                  file={file}
                  files={files}
                  changeFileName={changeFileName}
                  handleDownload={handleDownload}
                  handlePreview={handlePreview}
                  deleteFile={deleteFile}
                  canPreviewFileName={canPreviewFileName}
                />
              ))}
            </Typography>
          )}
        </div>
      )}
      <DocumentsPreviewer
        open={previewOpen}
        onClose={handleClosePreview}
        fileName={currentPreviewFile ?? ''}
        blobUrl={blobUrl}
        contentType={contentType}
        loading={previewLoading}
        error={previewError}
        onDownload={() => handleDownload(currentPreviewFile ?? '')}
        onError={(error) => setPreviewError(error)}
      />
    </div>
  );
};

export default FilesDialog;
